import { resolveParticipantId } from './participantIds'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeBoolean(value, fallbackValue = false) {
  return typeof value === 'boolean' ? value : fallbackValue
}

export const PARTICIPANT_CONNECTION_STATES = Object.freeze({
  joining: 'joining',
  connected: 'connected',
  reconnecting: 'reconnecting',
  left: 'left',
  failed: 'failed',
})

/**
 * @param {string | null | undefined} connectionState
 * @param {string | null} [fallbackValue=null]
 * @returns {string | null}
 */
export function normalizeParticipantConnectionState(
  connectionState,
  fallbackValue = null,
) {
  const normalizedConnectionState = normalizeOptionalString(connectionState)

  if (!normalizedConnectionState) {
    return fallbackValue
  }

  return Object.values(PARTICIPANT_CONNECTION_STATES).includes(normalizedConnectionState)
    ? normalizedConnectionState
    : fallbackValue
}

/**
 * @param {object} [transport={}]
 * @returns {object}
 */
export function createSessionParticipantTransport(transport = {}) {
  return isPlainObject(transport) ? { ...transport } : {}
}

/**
 * Provider-neutral participant model consumed by the shared presentation shell.
 *
 * @typedef {object} SessionParticipant
 * @property {string} id
 * @property {string | null} userId
 * @property {string | null} actorType
 * @property {string | null} actorId
 * @property {string} displayName
 * @property {string | null} profilePicId
 * @property {string | null} role
 * @property {boolean} isLocal
 * @property {string | null} connectionState
 * @property {boolean} audioMuted
 * @property {boolean} videoMuted
 * @property {boolean} screenSharing
 * @property {*} videoHandle
 * @property {string} renderKey
 * @property {object} transport
 */

/**
 * Creates a normalized provider-neutral participant object.
 *
 * @param {Partial<SessionParticipant> & {
 *   participantId?: string | null,
 *   stableActorId?: string | null,
 * }} [participant={}]
 * @returns {SessionParticipant}
 */
export function createSessionParticipant(participant = {}) {
  const resolvedParticipantId = resolveParticipantId(participant)

  if (!resolvedParticipantId) {
    throw new Error(
      '[live/domain] SessionParticipant requires a canonical id or enough identity fields to derive one.',
    )
  }

  return {
    id: resolvedParticipantId,
    userId: normalizeOptionalString(participant?.userId),
    actorType: normalizeOptionalString(participant?.actorType),
    actorId: normalizeOptionalString(
      participant?.actorId ?? participant?.stableActorId,
    ),
    displayName: normalizeOptionalString(participant?.displayName, '') || '',
    profilePicId: normalizeOptionalString(
      participant?.profilePicId ?? participant?.avatarImageId,
    ),
    role: normalizeOptionalString(participant?.role),
    isLocal: normalizeBoolean(participant?.isLocal, false),
    connectionState: normalizeParticipantConnectionState(participant?.connectionState),
    audioMuted: normalizeBoolean(participant?.audioMuted, false),
    videoMuted: normalizeBoolean(participant?.videoMuted, false),
    screenSharing: normalizeBoolean(participant?.screenSharing, false),
    videoHandle: participant?.videoHandle ?? null,
    renderKey:
      normalizeOptionalString(participant?.renderKey, resolvedParticipantId) ||
      resolvedParticipantId,
    transport: createSessionParticipantTransport(participant?.transport),
  }
}
